import { useCallback, useEffect, useState } from 'react';
import Filters from 'components/Filters';
import Searchbar from 'components/Table/SearchBar';
import { ColumnFiltersState } from '@tanstack/react-table';
import { getUsers } from 'api/users/api';
import {
  GetUsersParams,
  User,
  UserMetaDataResponse,
  UsersResponse,
} from '../../api/users/schemas/Users';
import { startOfDay, subMonths } from 'date-fns';
import { usePaginatedTable } from 'hooks/usePaginatedTable';
import { useFilterForTable } from 'hooks/useFilterForTable';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import { SingleValue } from 'react-select';
import { dateToUTCWithoutMS } from 'shared/util/dateToUtcStrWithoutMS';
import { useSortedTable } from 'hooks/useSortedTable';
import UsersHeader from './UsersHeader';
import InviteManagementTable from './InviteManagementTable';
import ExistingUsers from './ExistingUsers';
import { getTextForSystemRole } from 'screens/RolesPermissions/CustomRoles/utils';

const statusOptions = [
  {
    value: 'ACTIVE',
    label: 'Active',
  },
  {
    value: 'DEPROVISIONED',
    label: 'Deprovisioned',
  },
  {
    value: 'LOCKED_OUT',
    label: 'Locked out',
  },
  {
    value: 'PASSWORD_EXPIRED',
    label: 'Password expired',
  },
  {
    value: 'PROVISIONED',
    label: 'Provisioned',
  },
  {
    value: 'RECOVERY',
    label: 'Recovery',
  },
  {
    value: 'STAGED',
    label: 'Staged',
  },
  {
    value: 'SUSPENDED',
    label: 'Suspended',
  },
];

const filterByDate = [
  {
    value: dateToUTCWithoutMS(startOfDay(subMonths(new Date(), 1))),
    label: 'Past month',
  },
  {
    value: dateToUTCWithoutMS(startOfDay(subMonths(new Date(), 3))),
    label: 'Past 3 months',
  },
];

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState<
    'existingUsers' | 'pendingInvitations'
  >('existingUsers');

  const [filterState, setFilterState] = useState<ColumnFiltersState>([]);
  const [search, setSearch] = useState<string | undefined>();
  const [dateFilter, setDateFilter] = useState<SingleValue<OptionType>>(null);

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'created_at', desc: true }],
  });

  const clearFilters = () => {
    setFilterState([]);
    clearroleFilter();
    clearStatusFilter();
    setDateFilter(null);
  };

  const {
    queryParamFilter: roleFilter,
    clearFilter: clearroleFilter,
    ...roleFilterProps
  } = useFilterForTable({ queryParamKey: 'role' });

  const {
    queryParamFilter: statusFilter,
    clearFilter: clearStatusFilter,
    ...statusFilterProps
  } = useFilterForTable({ queryParamKey: 'status' });

  const getUsersQueryFn = useCallback(
    (args?: GetUsersParams) =>
      getUsers({
        ...(search ? { search } : {}),
        ...roleFilter,
        ...sortingQueryParams,
        ...statusFilter,
        ...(dateFilter
          ? {
              date_added: dateFilter.value,
            }
          : {}),
        ...args,
      }),
    [dateFilter, roleFilter, search, sortingQueryParams, statusFilter],
  );

  const {
    data: tablePageData,
    metadata,
    ...paginatedTableProps
  } = usePaginatedTable<User, UserMetaDataResponse>(
    getUsersQueryFn,
    {
      search,
      dateFilter,
      roleFilter,
      statusFilter,
      sortingQueryParams,
    },
    [
      'users',
      {
        search,
        dateFilter,
        roleFilter,
        statusFilter,
        sortingQueryParams,
      },
    ],
    {
      refetchOnMount: true,
      onSuccess: (data: UsersResponse) =>
        onRolesOptionsChange(data.metadata.roles ?? []),
    },
  );

  const [rolesOptions, setRolesOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const onRolesOptionsChange = (roles: string[]) =>
    setRolesOptions(
      roles.map((role) => ({
        value: role,
        label: getTextForSystemRole(role),
      })),
    );

  useEffect(() => {
    if (activeTab === 'existingUsers')
      onRolesOptionsChange(metadata?.roles ?? []);
    else setRolesOptions([]);
  }, [activeTab, metadata?.roles]);

  return (
    <div className="w-full p-4 sm:p-8">
      <div className="user-management mx-auto flex w-full flex-col items-start justify-start">
        <UsersHeader
          onChangeActiveTab={(tab) => {
            setActiveTab(tab);
            clearFilters();
          }}
        />
        <div className="flex w-full flex-col items-start justify-start">
          <div className="mb-12 flex w-full flex-wrap items-start justify-between gap-4">
            <div className="w-full sm:flex-1">
              <Searchbar
                placeholder="Search by name, email, or phone number"
                clearAllValues={!search}
                onQueryClear={() => setSearch(undefined)}
                onSearch={setSearch}
              />
            </div>
            <Filters clearFilters={clearFilters}>
              <GraniteSelect
                placeholder="Filter user role"
                options={rolesOptions}
                isMulti
                {...roleFilterProps}
              />
              {activeTab === 'existingUsers' && (
                <GraniteSelect
                  placeholder="Filter user status"
                  options={statusOptions}
                  isMulti
                  {...statusFilterProps}
                />
              )}
              <GraniteSelect
                options={filterByDate}
                className="col-span-2 md:col-span-1 xl:col-span-1"
                placeholder="Filter ticket by date"
                isClearable={false}
                isSearchable={false}
                controlShouldRenderValue={false}
                onChange={setDateFilter}
                value={dateFilter}
              />
            </Filters>
          </div>
          {activeTab === 'existingUsers' ? (
            <div className="product-tour-my-company-edit-and-delete-users w-full">
              <ExistingUsers
                tablePageData={tablePageData as User[]}
                sortingState={sortingState}
                onSortingChange={onSortingChange}
                paginatedTableProps={paginatedTableProps}
              />
            </div>
          ) : (
            <InviteManagementTable
              columnFilterState={[
                ...filterState,
                ...(roleFilter?.role
                  ? [{ id: 'role', value: roleFilter.role }]
                  : []),
                ...(dateFilter?.value
                  ? [{ id: 'created_at', value: dateFilter.value }]
                  : []),
              ]}
              onRolesOptionsChange={onRolesOptionsChange}
              onColumnFiltersChange={setFilterState}
              globalFilterState={search}
              onGlobalFilterChange={setSearch}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
